import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Calendar, Clock, MapPin, CheckCircle, AlertCircle, XCircle, HelpCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type AvailabilityStatus = 'free' | 'busy' | 'tentative' | 'unknown' | 'not_connected' | 'checking';

interface AvailabilityIndicatorProps {
  eventId?: string;
  guestId?: string;
  optionId?: string;
  status?: AvailabilityStatus;
  type: 'date' | 'time' | 'location';
  value: string;
  conflictDetails?: string;
  showIcon?: boolean;
  showDetails?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onStatusChange?: (status: AvailabilityStatus) => void;
  enableLiveCheck?: boolean;
}

export const AvailabilityIndicator = ({
  eventId,
  guestId,
  optionId,
  status: propStatus,
  type,
  value,
  conflictDetails,
  showIcon = true,
  showDetails = false,
  size = 'md',
  onStatusChange,
  enableLiveCheck = false
}: AvailabilityIndicatorProps) => {
  const [status, setStatus] = useState<AvailabilityStatus>(propStatus || 'unknown');
  const [isChecking, setIsChecking] = useState(false);
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [confidenceScore, setConfidenceScore] = useState<number>(0);
  const [lastChecked, setLastChecked] = useState<string | null>(null);
  const { toast } = useToast();

  const getIcon = () => {
    switch (type) {
      case 'date':
        return <Calendar className="w-3 h-3" />;
      case 'time':
        return <Clock className="w-3 h-3" />;
      case 'location':
        return <MapPin className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const getStatusConfig = () => {
    switch (status) {
      case 'free':
        return {
          variant: 'default' as const,
          color: 'text-green-700 dark:text-green-300',
          bg: 'bg-green-100 dark:bg-green-900/30',
          border: 'border-green-300 dark:border-green-700',
          icon: <CheckCircle className="w-3 h-3" />,
          label: 'Available',
          description: 'You\'re free during this time'
        };
      case 'busy':
        return {
          variant: 'destructive' as const,
          color: 'text-red-700 dark:text-red-300',
          bg: 'bg-red-100 dark:bg-red-900/30',
          border: 'border-red-300 dark:border-red-700',
          icon: <XCircle className="w-3 h-3" />,
          label: 'Busy',
          description: conflictDetails || 'You have conflicts during this time'
        };
      case 'tentative':
        return {
          variant: 'secondary' as const,
          color: 'text-orange-700 dark:text-orange-300',
          bg: 'bg-orange-100 dark:bg-orange-900/30',
          border: 'border-orange-300 dark:border-orange-700',
          icon: <AlertCircle className="w-3 h-3" />,
          label: 'Maybe',
          description: 'You have tentative events during this time'
        };
      case 'checking':
        return {
          variant: 'outline' as const,
          color: 'text-blue-600 dark:text-blue-400',
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-300 dark:border-blue-700',
          icon: <Loader2 className="w-3 h-3 animate-spin" />,
          label: 'Checking...',
          description: 'Checking your calendar availability'
        };
      case 'unknown':
        return {
          variant: 'outline' as const,
          color: 'text-gray-600 dark:text-gray-400',
          bg: 'bg-gray-100 dark:bg-gray-800',
          border: 'border-gray-300 dark:border-gray-600',
          icon: <HelpCircle className="w-3 h-3" />,
          label: 'Unknown',
          description: 'Unable to check availability for this time'
        };
      case 'not_connected':
      default:
        return {
          variant: 'outline' as const,
          color: 'text-muted-foreground',
          bg: 'bg-muted',
          border: 'border-muted',
          icon: null,
          label: '',
          description: 'Connect your calendar to see availability'
        };
    }
  };

  const checkAvailability = async () => {
    if (!enableLiveCheck || !eventId || !guestId || type === 'location') {
      return;
    }

    setIsChecking(true);
    setStatus('checking');

    try {
      // Convert option value to start/end times
      let startTime: string;
      let endTime: string;

      if (type === 'date') {
        const date = new Date(value);
        startTime = new Date(date.setHours(9, 0, 0, 0)).toISOString();
        endTime = new Date(date.setHours(17, 0, 0, 0)).toISOString();
      } else if (type === 'time') {
        const today = new Date();
        const [hours, minutes] = value.split(':').map(Number);
        const startDate = new Date(today.setHours(hours, minutes, 0, 0));
        const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour duration
        startTime = startDate.toISOString();
        endTime = endDate.toISOString();
      } else {
        setStatus('unknown');
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-availability', {
        body: {
          event_id: eventId,
          guest_id: guestId,
          start_time: startTime,
          end_time: endTime,
          buffer_minutes: 15,
          check_travel_time: false
        }
      });

      if (error) {
        console.error('Availability check error:', error);
        setStatus('unknown');
        return;
      }

      if (data) {
        const newStatus = data.availability_status as AvailabilityStatus;
        setStatus(newStatus);
        setConflicts(data.conflicts || []);
        setConfidenceScore(data.confidence_score || 0);
        setLastChecked(data.last_checked);
        
        onStatusChange?.(newStatus);
      }

    } catch (error) {
      console.error('Error checking availability:', error);
      setStatus('unknown');
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    if (propStatus) {
      setStatus(propStatus);
    } else if (enableLiveCheck && eventId && guestId && type !== 'location') {
      checkAvailability();
    }
  }, [eventId, guestId, optionId, type, value, enableLiveCheck, propStatus]);

  const config = getStatusConfig();

  if (status === 'not_connected') {
    return (
      <div className="flex items-center gap-2">
        {showIcon && getIcon()}
        <span className={size === 'sm' ? 'text-sm' : 'text-base'}>{value}</span>
      </div>
    );
  }

  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-xs',
    md: 'px-2 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };

  const content = (
    <div className={`inline-flex items-center gap-2 rounded-md border ${config.bg} ${config.border} ${sizeClasses[size]}`}>
      {showIcon && getIcon()}
      <span className={size === 'sm' ? 'text-sm' : 'text-base'}>{value}</span>
      {config.icon && <span className={config.color}>{config.icon}</span>}
      {(config.label && showDetails) && (
        <Badge 
          variant={config.variant} 
          className="ml-1 text-xs px-1.5 py-0.5"
        >
          {config.label}
          {confidenceScore > 0 && confidenceScore < 1 && (
            <span className="ml-1 opacity-75">
              ({Math.round(confidenceScore * 100)}%)
            </span>
          )}
        </Badge>
      )}
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {content}
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-2">
            <p className="font-medium">{config.description}</p>
            {conflicts.length > 0 && (
              <div className="text-xs text-muted-foreground">
                <p>{conflicts.length} conflict(s) detected</p>
              </div>
            )}
            {lastChecked && (
              <p className="text-xs text-muted-foreground">
                Last checked: {new Date(lastChecked).toLocaleTimeString()}
              </p>
            )}
            {confidenceScore > 0 && (
              <p className="text-xs text-muted-foreground">
                Confidence: {Math.round(confidenceScore * 100)}%
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};